'use client';

import React, { createContext, useContext, useEffect, useState } from 'react';
import { getContainer } from '@framework/di/getContainer';
import { usePageFiltersContext } from '@/creditflowresearch/application/PageFilters/PageFiltersContext';
import { prepareTopPeriodsPayload } from '@/creditflowresearch/application/TopCompanies/helpers';
import CompanyRepository from '@/creditflowresearch/infrastructure/repository/CompanyRepository';
import useExportButtonWithSpinner from '../useExportButtonWithSpinner';
import { useHeaderMenuContext } from '@/creditflowresearch/application/HeaderMenu/HeaderMenuContext';
import { ISSUER_NAME } from '@/creditflowresearch/ui/components/TopCompanies/constants';
import { IParent } from '@/creditflowresearch/domain/vo/IDeal';

interface IPeriod {
  dateFrom: string,
  dateTo: string | null,
  disabled: boolean,
  label: string,
  value: string,
}

interface ITopParents {
  periods: {
    collection: IPeriod[],
    hasServerError: boolean,
  },
  totalCount: number,
  includedRole: string,
  parent: IParent,
}

export const useTopCompanyCommon = ({
  periods: {
    collection: serverPeriodsCollection,
    hasServerError,
  },
  totalCount,
  parent,
}: ITopParents) => {
  const container = getContainer();
  const {
    isExportFetching,
    isExportAvailable,
    downloadExcelFile,
    setIsExportAvailable,
  } = useExportButtonWithSpinner(totalCount !== 0);
  const { regions: regionsList, products: productsList } = useHeaderMenuContext();
  const [periodsCollection, setPeriodsCollection] = useState(serverPeriodsCollection);
  const [isPeriodsFetching, setIsPeriodsFetching] = useState<boolean>(true);
  const [selectedPeriod, setSelectedPeriod] = useState<IPeriod>(serverPeriodsCollection[0] || null);
  const [activeTab, setActiveTab] = useState<string>(ISSUER_NAME);
  const { pageFilters, includedProductIds, includedRegionIds } = usePageFiltersContext();

  const payload = prepareTopPeriodsPayload({
    ...pageFilters,
  });

  const getPeriodsCollection = async (): Promise<void> => {
    const companyRepository = container.get(CompanyRepository);
    setIsPeriodsFetching(false);
    try {
      const response = await companyRepository.getTopCompaniesPeriods(payload);
      setPeriodsCollection(response.map((filter) => ({
        value: filter.filterName,
        label: filter.filterName,
        dateFrom: filter.dateFrom,
        dateTo: filter.dateTo,
        disabled: !filter.active,
      })));
    } finally {
      setIsPeriodsFetching(true);
    }
  };

  useEffect(() => {
    if (hasServerError) {
      getPeriodsCollection();
    }
  }, [hasServerError]);

  useEffect(() => {
    if (!hasServerError) {
      setPeriodsCollection(serverPeriodsCollection);
      setSelectedPeriod(serverPeriodsCollection[0] || null);
    }
  }, [serverPeriodsCollection]);

  const exportToExcel = () => downloadExcelFile({
    pageFilters: {
      ...pageFilters,
      includedProductIds,
      includedRegionIds,
    },
    selectedPeriod,
    products: productsList,
    regions: regionsList,
    parentName: parent?.name,
  });

  return {
    isPeriodsFetching,
    periodsCollection,
    setSelectedPeriod,
    selectedPeriod,
    isExportFetching,
    isExportAvailable,
    setIsExportAvailable,
    exportToExcel,
    activeTab,
    setActiveTab,
  };
};

type TopCompanyCommonContextType = ReturnType<typeof useTopCompanyCommon>;

export const TopCompanyCommonContext = createContext<TopCompanyCommonContextType>(null);

export const useTopCompanyCommonContext = () => {
  const context = useContext(TopCompanyCommonContext);
  if (!context) {
    throw new Error('useTopCompanyCommonContext must be used within the TopCompanyCommonContext');
  }
  return context;
};

interface IProps {
  topParents: ITopParents,
  children: React.ReactNode,
}

const TopCompanyCommonContextProvider = ({ children, topParents }: IProps) => (
  <TopCompanyCommonContext.Provider value={ useTopCompanyCommon(topParents) }>
    { children }
  </TopCompanyCommonContext.Provider>
);

export default TopCompanyCommonContextProvider;
